import { Injectable, EventEmitter } from '@angular/core';
import { from, Observable } from 'rxjs';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { AccountInfo, InteractionRequiredAuthError } from '@azure/msal-browser';

import { ConfigurationAAD } from '..';
import { ConstantService } from '../../shared/services/constant.service';
import { API_SCOPES, LOGIN_SCOPES, msalInstance } from './msal.config';

// Shape the app previously read from ADAL's userInfo.
export interface RootsUser {
  userName: string;
  profile: any;
}

@Injectable()
export class AuthenticationService {
  currentUser: RootsUser;
  loggedIn = false;
  userLoadedEvent: EventEmitter<RootsUser> = new EventEmitter<RootsUser>();
  protected aadConfiguration: ConfigurationAAD;
  private authHeaders: HttpHeaders;
  private _config: any;

  constructor(
    protected http: HttpClient,
    private _constService: ConstantService
  ) {
    this.aadConfiguration = new ConfigurationAAD(this._constService);
    this._config = this.aadConfiguration.config;
    this.loggedIn = this.isAuthenticated;
    if (this.loggedIn) {
      this._setTokenHeaders(this.idToken);
    }
  }

  get config(): any {
    return this._config;
  }

  get account(): AccountInfo | null {
    return msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0] ?? null;
  }

  get isAuthenticated(): boolean {
    return !!this.account;
  }

  get userInfo(): RootsUser | null {
    const account = this.account;
    return account ? { userName: account.username, profile: account.idTokenClaims } : null;
  }

  // Raw ID token of the Roots app registration; the backends expect this as the Bearer token.
  get idToken(): string {
    return this.account?.idToken ?? '';
  }

  get accessToken(): string {
    return this.idToken;
  }

  getUser() {
    if (this.isAuthenticated) {
      this.currentUser = this.userInfo;
      this.loggedIn = true;
      this.userLoadedEvent.emit(this.currentUser);
    }
  }

  // Returns a valid (silently renewed if expired) ID token.
  acquireToken(): Observable<string> {
    return from(this.getTokenForScopes(null));
  }

  // Bearer token for API calls (see API_SCOPES). Takes the URL so callers stay explicit about
  // where the token goes; every API currently gets the same token.
  getTokenForUrl(url: string): Promise<string> {
    return this.getTokenForScopes(API_SCOPES);
  }

  // scopes null -> ID token; otherwise an access token for those scopes.
  async getTokenForScopes(scopes: string[] | null): Promise<string> {
    const request = { scopes: scopes ?? LOGIN_SCOPES, account: this.account };
    try {
      const result = await msalInstance.acquireTokenSilent(request);
      return scopes ? result.accessToken : result.idToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        // Consent or a fresh sign-in is needed; MSAL navigates away and comes back.
        await msalInstance.acquireTokenRedirect(request);
      }
      throw error;
    }
  }

  sendToken(token: string) {
    localStorage.setItem('Bearer', token);
  }

  getToken() {
    return localStorage.getItem('Bearer');
  }

  login() {
    window.localStorage.clear();
    msalInstance.loginRedirect({ scopes: LOGIN_SCOPES });
  }

  logout() {
    const account = this.account;
    window.localStorage.clear();
    msalInstance.logoutRedirect({ account });
  }

  private _setTokenHeaders(access_token: string, token_type = 'Bearer') {
    localStorage.setItem(token_type, access_token);
    this.authHeaders = new HttpHeaders();
    this.authHeaders = this.authHeaders.set('Authorization', token_type + ' ' + access_token);
  }

  public _setRequestOptions(headers?: HttpHeaders) {
    if (headers) {
      headers.set('Authorization',
        this.authHeaders.get('Authorization'));
      headers.set('Uid',
        this.authHeaders.get('Uid'));
    } else {
      return this.authHeaders;
    }
    return headers;
  }

  AuthGet(url: string, headers?: HttpHeaders): Observable<HttpResponse<Object>> {
    if (headers) {
      headers = this._setRequestOptions(headers);
    } else {
      headers = this._setRequestOptions();
    }
    return this.http.get<HttpResponse<Object>>(url, { headers: headers });
  }
}

export interface ScoutUser {
  deleted: boolean;
  updatedAt: string;
  createdAt: string;
  version: string;
  id: string;
  isAvailable: number;
  basicDescription: string;
  basicName: string;
  code: string;
  username: string;
  contact_Email: string;
  contact_Cellphone: string;
  contact_Telephone: string;
  contact_Address: string;
  contact_Name: string;
  languageTag: string;
  systemUser_Username: string;
  regionID: string;
  password: string;
}
